import { observable } from "@legendapp/state";
import { ObservablePersistLocalStorage } from "@legendapp/state/persist-plugins/local-storage";
import { synced } from "@legendapp/state/sync";
import { type ChatMode, ChatModeSchema } from "@repo/shared/types/chat-mode";

/** Default width of the chat panel in pixels. Also acts as the minimum width. */
export const CHAT_PANEL_DEFAULT_WIDTH = 388;

/** Global state for the AI chat slide-over panel. */
export const chat$ = observable({
  /** Whether the chat panel is open. Persisted across page reloads. Defaults to open so first-time users discover the agent panel. */
  isOpen: synced<boolean>({
    initial: true,
    persist: {
      name: "chat-panel-open",
      plugin: ObservablePersistLocalStorage,
    },
  }),
  /** Current chat panel width in pixels. Persisted across page reloads. */
  width: synced<number>({
    initial: CHAT_PANEL_DEFAULT_WIDTH,
    persist: {
      name: "chat-panel-width",
      plugin: ObservablePersistLocalStorage,
    },
  }),
  /** When set, the AI chat picks up this prompt and sends it automatically. */
  pendingAiPrompt: null as string | null,
  /** Global chat mode observable. Resets to AUTO on thread deletion. */
  mode: "AUTO" as ChatMode,
  /**
   * Mirrors the active chat's streaming/submitted state. Lets surfaces outside
   * the `Chat` component (e.g. the full-page collapse button) avoid navigating
   * mid-stream, which would unmount `useChat` and drop the in-flight response.
   */
  isStreaming: false,
});

/** Opens the AI chat panel and sends an error-fix prompt. */
export const sendErrorToAiChat = (errorMessage: string) => {
  chat$.isOpen.set(true);
  chat$.pendingAiPrompt.set(
    `My SQL query is returning an error:\n\`${errorMessage}\`\nCan you help me fix it?`,
  );
};

/** Cycles the chat mode. */
export const cycleChatMode = () => {
  const modes = ChatModeSchema.options;
  chat$.mode.set(modes[(modes.indexOf(chat$.mode.get()) + 1) % modes.length]);
};
